// Adventure Editor Types - Extracted from Blazor DTOs
// Based on AdventureCore.Application.DTOs

import { WorkflowConditionType } from './workflow.types'

// ====================
// ENUMS
// ====================

export enum TagType {
  Interests = 1,
  GenderIdentity = 2,
  Adventures = 3,
  LookingFor = 4,
  AdventureTypes = 5,
  Moderation = 6,
}

export enum AdventureStatus {
  Draft = 1,
  Testing = 2,
  Published = 3,
  ModeratorReview = 4,
  Archived = 5,
  Suspended = 6,
}

// Helper to get display label for adventure status
export function getAdventureStatusLabel(status: number | string | undefined): string {
  if (status === undefined || status === null) return 'Unknown'

  const numStatus = typeof status === 'string' ? parseInt(status, 10) : status

  switch (numStatus) {
    case AdventureStatus.Draft: return 'Draft'
    case AdventureStatus.Testing: return 'Testing'
    case AdventureStatus.Published: return 'Published'
    case AdventureStatus.ModeratorReview: return 'Moderator Review'
    case AdventureStatus.Archived: return 'Archived'
    case AdventureStatus.Suspended: return 'Suspended'
    default: return String(status)
  }
}

// Helper to get chip color for adventure status
export function getAdventureStatusColor(status: number | string | undefined): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  if (status === undefined || status === null) return 'default'

  const numStatus = typeof status === 'string' ? parseInt(status, 10) : status

  switch (numStatus) {
    case AdventureStatus.Draft: return 'warning'
    case AdventureStatus.Testing: return 'info'
    case AdventureStatus.Published: return 'success'
    case AdventureStatus.ModeratorReview: return 'secondary'
    case AdventureStatus.Archived: return 'default'
    case AdventureStatus.Suspended: return 'error'
    default: return 'default'
  }
}

/**
 * Category ids the portal treats specially because each has its own engine. Every other category
 * is plain row data from the categories table; a new one needs no client change.
 */
export const TRADITIONAL_GEOCACHE_CATEGORY_ID = 1
export const TRAILS_CATEGORY_ID = 9

export enum LocationType {
  Physical = 'Physical',
  Virtual = 'Virtual',
  Checkpoint = 'Checkpoint',
  Decision = 'Decision',
}

export enum RequirementType {
  Generic = 'Generic',
  Geofence = 'Geofence',
  Reputation = 'Reputation',
  Rank = 'Rank',
  QRCode = 'QRCode',
  NumberCode = 'NumberCode',
  ShareCode = 'ShareCode',
  Question = 'Question',
  Weather = 'Weather',
  Group = 'Group',
  GenderIdentity = 'GenderIdentity',
  ModeratorConfirmation = 'ModeratorConfirmation',
  AdventureCompletion = 'AdventureCompletion',
}

// Must match C# enum values
// Creator = 0, Maintainer = 1, Tester = 2, Moderator = 3, SystemAdmin = 4, User = 5, Group = 6
export enum RoleType {
  Creator = 0,
  Maintainer = 1,
  Tester = 2,
  Moderator = 3,
  SystemAdmin = 4,
  User = 5,
  Group = 6,
}

// ====================
// GROUP PARTICIPATION ENUMS
// ====================

export enum VotingVisibility {
  GroupDefined = 0,
  HiddenUntilCompleted = 1,
  RealTime = 2,
}

export enum VoteChangePolicy {
  GroupDefined = 0,
  LockedAfterSubmission = 1,
  LimitedWindow = 2,
  AllowedUntilClose = 3,
}

export enum VotingCompletionMode {
  GroupDefined = 0,
  VotingPercentage = 1,
  TimerBased = 2,
}

export enum VotingTieResolution {
  GroupDefined = 0,
  TimeoutRandomSelection = 1,
  RevoteOnTiedOptions = 2,
}

export enum OfflineMemberAction {
  GroupDefined = 0,
  Pause = 1,
  Continue = 2,
  Abandon = 3,
  VoteOnAction = 4,
}

// ====================
// GROUP PARTICIPATION SETTINGS DTO
// ====================

export interface GroupParticipationSettingsDto {
  // Voting Visibility
  votingVisibility: VotingVisibility

  // Vote Change Policy
  voteChangePolicy: VoteChangePolicy
  voteChangeWindowSeconds?: number // 25-900 seconds (for LimitedWindow)

  // Voting Completion Settings
  votingCompletionMode: VotingCompletionMode
  votingCompletionPercentage?: number // 25-100%
  votingCompletionTimerSeconds?: number // 25-900 seconds

  // Voting Tie Resolution
  votingTieResolution: VotingTieResolution
  votingTieTimeoutSeconds?: number // 25-900 seconds

  // Offline Member Handling
  offlineMemberAction: OfflineMemberAction

  // Group Participation Boundaries (stored in feet - imperial)
  maxParticipantDistanceFeet?: number // Max distance between participants (stored in feet)
  participantProximityPercentage?: number // % of participants within distance (0-100)
}

// Helper labels for group participation enums
export const VOTING_VISIBILITY_LABELS: Record<VotingVisibility, string> = {
  [VotingVisibility.GroupDefined]: 'Group Defined',
  [VotingVisibility.HiddenUntilCompleted]: 'Hidden Until Completed',
  [VotingVisibility.RealTime]: 'Real Time',
}

export const VOTE_CHANGE_POLICY_LABELS: Record<VoteChangePolicy, string> = {
  [VoteChangePolicy.GroupDefined]: 'Group Defined',
  [VoteChangePolicy.LockedAfterSubmission]: 'Locked After Submission',
  [VoteChangePolicy.LimitedWindow]: 'Limited Window',
  [VoteChangePolicy.AllowedUntilClose]: 'Allowed Until Close',
}

export const VOTING_COMPLETION_MODE_LABELS: Record<VotingCompletionMode, string> = {
  [VotingCompletionMode.GroupDefined]: 'Group Defined',
  [VotingCompletionMode.VotingPercentage]: 'Voting Percentage',
  [VotingCompletionMode.TimerBased]: 'Timer Based',
}

export const VOTING_TIE_RESOLUTION_LABELS: Record<VotingTieResolution, string> = {
  [VotingTieResolution.GroupDefined]: 'Group Defined',
  [VotingTieResolution.TimeoutRandomSelection]: 'Timeout Random Selection',
  [VotingTieResolution.RevoteOnTiedOptions]: 'Revote On Tied Options',
}

export const OFFLINE_MEMBER_ACTION_LABELS: Record<OfflineMemberAction, string> = {
  [OfflineMemberAction.GroupDefined]: 'Group Defined',
  [OfflineMemberAction.Pause]: 'Pause',
  [OfflineMemberAction.Continue]: 'Continue',
  [OfflineMemberAction.Abandon]: 'Abandon',
  [OfflineMemberAction.VoteOnAction]: 'Vote On Action',
}

// ====================
// CORE DTOs
// ====================

export interface AdventureDto {
  /**
   * Standing inside the join geofence completes it. Meant for places, where arriving is the
   * whole activity; never for a trail, where it would finish the walk at the trailhead.
   *
   * Declared here, unlike the silent flags beside it in the editor, which are read through
   * casts because this interface never learned about them.
   */
  autoCheckIn?: boolean

  id: string
  title: string
  name?: string // Alias for backward compatibility
  description: string
  fullDescription: string
  quickDescription?: string
  status: AdventureStatus
  mainCategory: number
  startDate?: string
  endDate?: string
  completedDate?: string
  location: string
  latitude?: number
  longitude?: number
  creatorId: string
  creatorName: string
  createdByUserName: string
  createdDate: string
  participantCount: number
  isParticipant: boolean
  joinedDate?: string
  isRepeatable: boolean
  isPublic: boolean
  allowGroups: boolean
  maxParticipants: number
  difficultyLevel: number
  useDisplayForJoining: boolean
  tags: string[] // Array of tag GUIDs
  averageRating: number
  ratingCount: number
  thumbnailUrl?: string
  coverUrl?: string
  canManage?: boolean // Whether current user can manage this adventure
  /**
   * Bitmask of how this user relates to the adventure (see UserRelationType): maintainer,
   * moderator, tester, joined. Sent by both GET /adventures/{id} and /{id}/edit; it was simply
   * never declared here, so reading it was a type error even though the value arrives.
   */
  userRelationType?: number
  groupParticipationSettings?: GroupParticipationSettingsDto
  // Geocache-specific fields
  geocacheCode?: string
  geocacheCodeUrl?: string
}

// GPX Export configuration for adventure
export interface GpxExportConfig {
  enabled: boolean              // Master toggle for GPX export
  routeOrder: string[]          // Array of route node IDs in export order
  includeWaypoints: boolean     // Include location nodes as waypoints
  combinedFile: boolean         // Single GPX file vs separate files per route
}

// Default GPX export config
export const DEFAULT_GPX_EXPORT_CONFIG: GpxExportConfig = {
  enabled: false,
  routeOrder: [],
  includeWaypoints: true,
  combinedFile: true,
}

export interface AdventureGeneralInfoDto {
  /** The authority's own page for this place. Absolute http(s) only. */
  externalUrl?: string
  /** What the link is called: "National Park Service", "Tickets". */
  externalUrlLabel?: string
  title: string
  quickDescription?: string
  fullDescription?: string
  mainCategory: number
  startDate?: Date | null
  endDate?: Date | null
  latitude?: number | null
  longitude?: number | null
  isPublic: boolean
  allowGroups: boolean
  maxParticipants: number
  isRepeatable: boolean
  difficultyLevel: number
  groupParticipationSettings?: GroupParticipationSettingsDto
  // Silent adventure fields
  isSilentJoin?: boolean
  /**
   * Standing inside the join geofence completes the adventure.
   *
   * Not a silent flag despite sitting beside them: a check-in adventure keeps its map marker
   * and its place in a player's history — the only thing it gives up is the tap that starts it.
   */
  autoCheckIn?: boolean
  isSilentComplete?: boolean
  silentCompleteDurationMinutes?: number
  // GPX Export settings
  gpxExportConfig?: GpxExportConfig
}

// ====================
// TAG SYSTEM
// ====================

export interface TagDto {
  id: string
  name: string
  displayName: string
  description?: string
  icon?: string
  color?: string
  isActive: boolean
  sortOrder: number
  categoryId?: string
}

export interface TagCategoryDto {
  id: string
  name: string
  displayName: string
  description?: string
  icon?: string
  color?: string
  isActive: boolean
  displayOrder: number
  selectionMode: 'single' | 'multiple'
  validationRule?: string
  selectionRequired?: boolean
  tagType?: TagType
  tags: TagDto[]
}

export interface CategoryWithTagsDto extends TagCategoryDto {
  // Same as TagCategoryDto - keeping for backward compatibility
}

// ====================
// LOCATION FLOW
// ====================

export interface LocationConnection {
  id: string
  fromLocationId: string
  toLocationId: string
  name?: string
  description?: string
}

export interface LocationFlowData {
  id: string
  name: string
  description?: string
  type: LocationType
  order: number
  x: number // Canvas position X
  y: number // Canvas position Y
  isFirstLocation: boolean
  outgoingConnections: LocationConnection[]
  unlockRequirements: UnlockRequirement[]
  completionRewards: any[]
  clues: any[]
  clueDisclosureType?: string
  clueReleaseDelayMinutes?: number
}

// ====================
// REQUIREMENTS / CRITERIA
// ====================

export interface UnlockRequirement {
  id: string
  type?: RequirementType
  description?: string
  isOptional: boolean
  order: number
  logicGroupId?: string
  // Type-specific fields (populated based on type)
  [key: string]: any
}

export interface GeofenceRequirement extends UnlockRequirement {
  latitude: number
  longitude: number
  radiusInUserUnit: number
  distanceUnit: string
}

export interface ReputationRequirement extends UnlockRequirement {
  minReputation: number
  maxReputation: number
}

export interface RankRequirement extends UnlockRequirement {
  minRank: number
  maxRank?: number
}

export interface QRCodeRequirement extends UnlockRequirement {
  qrCodeUrl: string
}

export interface NumberCodeRequirement extends UnlockRequirement {
  code: string
}

export interface QuestionRequirement extends UnlockRequirement {
  question: string
  answer: string
  caseSensitive: boolean
}

export interface LogicGroup {
  id: string
  operator: 'AND' | 'OR'
  requirements: UnlockRequirement[]
  parentGroupId?: string
  order: number
}

// ====================
// TEAM MANAGEMENT
// ====================

export interface RankDto {
  tier: number
  name: string
  pointsRequired: number
  description: string
  badge: string
  cssClass: string
  themeFusion: string
}

export interface UserSearchDto {
  id: string
  userId: string
  username: string
  displayName: string
  profilePicture?: string
  profileImageUrl?: string
  avatar?: string
  location?: string
  homeTown?: string
  bio?: string
  isConnected?: boolean
  isOnline?: boolean
  rank?: RankDto
}

export interface AdventureMaintainerDto {
  id: string
  userId: string
  username: string
  userName?: string // Deprecated, use username
  displayName: string
  profileImageUrl?: string
  isCreator?: boolean
  addedDate: string
  rank?: RankDto
}

export interface AdventureModeratorDto {
  id: string
  userId: string
  username: string
  userName?: string // Deprecated, use username
  displayName: string
  profileImageUrl?: string
  isCreator?: boolean
  addedDate: string
  rank?: RankDto
}

export interface AdventureTesterDto {
  id: string
  userId: string
  username: string
  userName?: string // Deprecated, use username
  displayName: string
  profileImageUrl?: string
  isCreator?: boolean
  addedDate: string
  rank?: RankDto
}

// ====================
// EDITOR STATE
// ====================

export interface AdventureEditorState {
  // Core adventure data
  adventureId?: string
  adventure?: AdventureDto

  // Editable form data
  generalInfo: AdventureGeneralInfoDto

  // Tags and categories
  availableCategories: CategoryWithTagsDto[]
  adventureTypeCategories: TagCategoryDto[]
  isLoadingTags: boolean

  // Location flow
  locationFlow: LocationFlowData[]

  // Requirements
  displayCriteria: UnlockRequirement[]
  joinCriteria: UnlockRequirement[]
  useDisplayForJoining: boolean

  // UI state
  activeSection:
    | 'description'
    | 'settings'
    | 'tags'
    | 'maintainers'
    | 'moderators'
    | 'testers'
    | 'display'
    | 'join'
    | 'locationflow'
    | 'mapview'
  isLoading: boolean
  isDirty: boolean
}

// ====================
// API REQUEST/RESPONSE
// ====================

export interface SaveAdventureRequest {
  generalInfo: AdventureGeneralInfoDto
  tags: string[]
}

export interface SaveTagsRequest {
  tagIds: string[]
}

export interface SaveCriteriaRequest {
  criteria: UnlockRequirement[]
}

export interface SaveLocationFlowRequest {
  locations: LocationFlowData[]
}

export interface AddTeamMemberRequest {
  adventureId: string
  userId?: string
  groupId?: string
  roleType: RoleType
  addedByUserId: string
}

// ====================
// HELPER TYPES
// ====================

export const LOCATION_TYPE_ICONS: Record<LocationType, string> = {
  [LocationType.Physical]: '📍',
  [LocationType.Virtual]: '💻',
  [LocationType.Checkpoint]: '✅',
  [LocationType.Decision]: '🔀',
}

export const LOCATION_TYPE_COLORS: Record<LocationType, string> = {
  [LocationType.Physical]: '#4CAF50',
  [LocationType.Virtual]: '#2196F3',
  [LocationType.Checkpoint]: '#FF9800',
  [LocationType.Decision]: '#9C27B0',
}

export const DIFFICULTY_LABELS: Record<number, string> = {
  1: 'Easy',
  2: 'Moderate',
  3: 'Intermediate',
  4: 'Advanced',
  5: 'Expert',
}

// ====================
// CRITERIA (Display/Join Requirements)
// ====================

/**
 * Represents a single criterion item for display/join requirements.
 * Uses WorkflowConditionType to leverage existing condition configuration UI.
 */
export interface CriteriaItem {
  id: string
  conditionType: WorkflowConditionType
  config: Record<string, unknown>
  logicGroupId?: string
  order: number
}

/**
 * Represents a logic group that combines criteria with AND/OR operators.
 * Items within a group are evaluated with the group's operator (isAnd).
 * Multiple groups are combined with OR (any group passing = overall pass).
 */
export interface CriteriaLogicGroup {
  id: string
  isAnd: boolean // true = AND (all criteria must pass), false = OR (any criteria can pass)
  orderIndex: number
}

/**
 * Helper to get display operator from isAnd boolean
 */
export function getOperatorDisplay(isAnd: boolean): 'AND' | 'OR' {
  return isAnd ? 'AND' : 'OR'
}

/**
 * Helper to convert operator to isAnd boolean
 */
export function operatorToIsAnd(operator: 'AND' | 'OR'): boolean {
  return operator === 'AND'
}

/**
 * Criteria types available for display requirements.
 * These match WorkflowConditionType but exclude input-based conditions
 * (NumericInput, QuestionAnswer, QRCode, ModeratorApproval) which require user interaction.
 */
export const AVAILABLE_DISPLAY_CRITERIA_TYPES: WorkflowConditionType[] = [
  // Weather conditions
  WorkflowConditionType.WeatherTemperature,
  WorkflowConditionType.WeatherHumidity,
  WorkflowConditionType.WeatherDayNight,
  WorkflowConditionType.WeatherWindSpeed,
  WorkflowConditionType.WeatherWindDirection,
  WorkflowConditionType.WeatherCloudCover,
  WorkflowConditionType.WeatherVisibility,
  WorkflowConditionType.WeatherSunsetSunrise,
  WorkflowConditionType.WeatherCondition,
  // Celestial conditions
  WorkflowConditionType.CelestialSolarNoon,
  WorkflowConditionType.CelestialTwilight,
  WorkflowConditionType.CelestialMoonRiseSet,
  WorkflowConditionType.CelestialMoonPhase,
  WorkflowConditionType.CelestialMoonAge,
  // Group/User conditions
  WorkflowConditionType.GroupMembership,
  // Adventure conditions
  WorkflowConditionType.AdventureCollectionCompletion,
  WorkflowConditionType.AdventureCompleted,
  // Location conditions
  WorkflowConditionType.Geofence,
  // Time conditions
  WorkflowConditionType.TimeSchedule,
  // User Tag conditions (display only - not available for join criteria)
  WorkflowConditionType.UserInterests,
  WorkflowConditionType.UserGenderIdentity,
  // User stats
  WorkflowConditionType.UserRank,
]

/**
 * Criteria types available for join requirements.
 * Excludes User Tag conditions (UserInterests, UserGenderIdentity) which are display-only.
 */
export const AVAILABLE_JOIN_CRITERIA_TYPES: WorkflowConditionType[] = [
  // Weather conditions
  WorkflowConditionType.WeatherTemperature,
  WorkflowConditionType.WeatherHumidity,
  WorkflowConditionType.WeatherDayNight,
  WorkflowConditionType.WeatherWindSpeed,
  WorkflowConditionType.WeatherWindDirection,
  WorkflowConditionType.WeatherCloudCover,
  WorkflowConditionType.WeatherVisibility,
  WorkflowConditionType.WeatherSunsetSunrise,
  WorkflowConditionType.WeatherCondition,
  // Celestial conditions
  WorkflowConditionType.CelestialSolarNoon,
  WorkflowConditionType.CelestialTwilight,
  WorkflowConditionType.CelestialMoonRiseSet,
  WorkflowConditionType.CelestialMoonPhase,
  WorkflowConditionType.CelestialMoonAge,
  // Group/User conditions
  WorkflowConditionType.GroupMembership,
  // Adventure conditions
  WorkflowConditionType.AdventureCollectionCompletion,
  WorkflowConditionType.AdventureCompleted,
  // Location conditions
  WorkflowConditionType.Geofence,
  // Time conditions
  WorkflowConditionType.TimeSchedule,
  // User stats
  WorkflowConditionType.UserRank,
]

/**
 * GeoCache-specific criteria types.
 * GeoCaches only allow: Time/Schedule, GeoFence, Adventure/Collection Completion, DayNight
 */
export const GEOCACHE_CRITERIA_TYPES: WorkflowConditionType[] = [
  // Time conditions
  WorkflowConditionType.TimeSchedule,
  // Location conditions
  WorkflowConditionType.Geofence,
  // Adventure conditions
  WorkflowConditionType.AdventureCollectionCompletion,
  WorkflowConditionType.AdventureCompleted,
  // Day/Night only (not other weather conditions)
  WorkflowConditionType.WeatherDayNight,
]

/**
 * Check if an adventure category is a GeoCache type
 */
export function isGeoCacheCategory(category: number | undefined): boolean {
  return category === TRADITIONAL_GEOCACHE_CATEGORY_ID
}

/**
 * GeoCache adventures have fixed settings that cannot be changed:
 * - Always public
 * - No group joins allowed
 * - Non-repeatable
 * - No workflow required
 */
export interface GeoCacheRestrictions {
  isPublic: true
  allowGroups: false
  isRepeatable: false
  requiresWorkflow: false
}

export const GEOCACHE_RESTRICTIONS: GeoCacheRestrictions = {
  isPublic: true,
  allowGroups: false,
  isRepeatable: false,
  requiresWorkflow: false,
}

/**
 * Check if an adventure category is a Trail type
 */
export function isTrailCategory(category: number | undefined): boolean {
  return category === TRAILS_CATEGORY_ID
}

/**
 * Trail adventures have these characteristics:
 * - Fully configurable settings (creator controls public/private, groups, repeatable)
 * - Requires workflow (must have at least one waypoint/checkpoint)
 * - Trailcraft-only rating
 * - Has per-adventure leaderboard for completion times
 */
export interface TrailRestrictions {
  requiresWorkflow: true
  ratingCategories: readonly ['trailcraft']
  hasLeaderboard: true
}

export const TRAIL_RESTRICTIONS: TrailRestrictions = {
  requiresWorkflow: true,
  ratingCategories: ['trailcraft'] as const,
  hasLeaderboard: true,
}

/**
 * Get the available criteria types based on adventure category
 */
export function getAvailableCriteriaTypes(
  category: number | undefined,
  criteriaType: 'display' | 'join'
): WorkflowConditionType[] {
  if (isGeoCacheCategory(category)) {
    return GEOCACHE_CRITERIA_TYPES
  }
  return criteriaType === 'display' ? AVAILABLE_DISPLAY_CRITERIA_TYPES : AVAILABLE_JOIN_CRITERIA_TYPES
}

/**
 * Legacy alias for backwards compatibility
 * @deprecated Use AVAILABLE_DISPLAY_CRITERIA_TYPES or AVAILABLE_JOIN_CRITERIA_TYPES instead
 */
export const AVAILABLE_CRITERIA_TYPES = AVAILABLE_DISPLAY_CRITERIA_TYPES

/**
 * Category groupings for criteria types in the add dropdown.
 */
export interface CriteriaCategoryGroup {
  name: string
  icon: string
  types: WorkflowConditionType[]
}

export const CRITERIA_CATEGORIES: CriteriaCategoryGroup[] = [
  {
    name: 'Weather',
    icon: '🌤️',
    types: [
      WorkflowConditionType.WeatherTemperature,
      WorkflowConditionType.WeatherHumidity,
      WorkflowConditionType.WeatherDayNight,
      WorkflowConditionType.WeatherWindSpeed,
      WorkflowConditionType.WeatherWindDirection,
      WorkflowConditionType.WeatherCloudCover,
      WorkflowConditionType.WeatherVisibility,
      WorkflowConditionType.WeatherSunsetSunrise,
      WorkflowConditionType.WeatherCondition,
    ],
  },
  {
    name: 'Celestial',
    icon: '🌙',
    types: [
      WorkflowConditionType.CelestialSolarNoon,
      WorkflowConditionType.CelestialTwilight,
      WorkflowConditionType.CelestialMoonRiseSet,
      WorkflowConditionType.CelestialMoonPhase,
      WorkflowConditionType.CelestialMoonAge,
    ],
  },
  {
    name: 'Group',
    icon: '👥',
    types: [WorkflowConditionType.GroupMembership],
  },
  {
    name: 'Adventure',
    icon: '🗺️',
    types: [
      WorkflowConditionType.AdventureCollectionCompletion,
      WorkflowConditionType.AdventureCompleted,
    ],
  },
  {
    name: 'Location',
    icon: '📍',
    types: [WorkflowConditionType.Geofence],
  },
  {
    name: 'Time',
    icon: '🕐',
    types: [WorkflowConditionType.TimeSchedule],
  },
  {
    name: 'User Tags',
    icon: '🏷️',
    types: [
      WorkflowConditionType.UserInterests,
      WorkflowConditionType.UserGenderIdentity,
    ],
  },
  {
    name: 'User Stats',
    icon: '🏅',
    types: [WorkflowConditionType.UserRank],
  },
]

/**
 * Get the icon for a condition type.
 */
export function getCriteriaTypeIcon(type: WorkflowConditionType | number): string {
  // Ensure numeric comparison (API may return number, not enum)
  const numericType = typeof type === 'number' ? type : Number(type)
  switch (numericType) {
    case WorkflowConditionType.WeatherTemperature:
      return '🌡️'
    case WorkflowConditionType.WeatherHumidity:
      return '💧'
    case WorkflowConditionType.WeatherDayNight:
      return '🌙'
    case WorkflowConditionType.WeatherWindSpeed:
      return '💨'
    case WorkflowConditionType.WeatherWindDirection:
      return '🧭'
    case WorkflowConditionType.WeatherCloudCover:
      return '☁️'
    case WorkflowConditionType.WeatherVisibility:
      return '👁️'
    case WorkflowConditionType.WeatherSunsetSunrise:
      return '🌅'
    case WorkflowConditionType.WeatherCondition:
      return '⛈️'
    // Celestial conditions
    case WorkflowConditionType.CelestialSolarNoon:
      return '☀️'
    case WorkflowConditionType.CelestialTwilight:
      return '🌆'
    case WorkflowConditionType.CelestialMoonRiseSet:
      return '🌕'
    case WorkflowConditionType.CelestialMoonPhase:
      return '🌓'
    case WorkflowConditionType.CelestialMoonAge:
      return '🌑'
    case WorkflowConditionType.GroupMembership:
      return '👥'
    case WorkflowConditionType.AdventureCollectionCompletion:
      return '📚'
    case WorkflowConditionType.AdventureCompleted:
      return '✅'
    case WorkflowConditionType.Geofence:
      return '📍'
    case WorkflowConditionType.TimeSchedule:
      return '🕐'
    case WorkflowConditionType.UserInterests:
      return '💜'
    case WorkflowConditionType.UserGenderIdentity:
      return '🪪'
    case WorkflowConditionType.UserRank:
      return '🏅'
    default:
      return '❓'
  }
}

/**
 * Criteria types that require a paid subscription.
 * Free users can only use criteria NOT in this set.
 */
export const SUBSCRIPTION_REQUIRED_CRITERIA: Set<number> = new Set([
  WorkflowConditionType.WeatherTemperature,      // 100
  WorkflowConditionType.WeatherHumidity,         // 101
  WorkflowConditionType.WeatherWind,             // 104 (deprecated)
  WorkflowConditionType.WeatherCloudCover,       // 105
  WorkflowConditionType.WeatherVisibility,       // 106
  WorkflowConditionType.WeatherWindSpeed,        // 107
  WorkflowConditionType.WeatherWindDirection,    // 108
  WorkflowConditionType.WeatherCondition,        // 109
  WorkflowConditionType.CelestialTwilight,       // 111
  WorkflowConditionType.CelestialMoonRiseSet,    // 112
  WorkflowConditionType.CelestialMoonPhase,      // 113
  WorkflowConditionType.CelestialMoonAge,        // 114
])

/**
 * Check if a criteria type requires a paid subscription.
 */
export function isSubscriptionRequired(type: WorkflowConditionType | number): boolean {
  const numericType = typeof type === 'number' ? type : Number(type)
  return SUBSCRIPTION_REQUIRED_CRITERIA.has(numericType)
}
